// Dashboard: at-a-glance metrics across the workflow. Read-only; it writes
// nothing.
//
// The port's biggest change is here. This screen used to pull every active
// invoice, every terminal invoice in the window, and all their decisions,
// flags, line items and corrections into the browser and compute the bars from
// them. Now each panel is an aggregation endpoint (docs/app.md → Analytics):
// six calls, each already scoped by the API to the rows this user may see, and
// each carrying an `as_of` stamp. The client only labels and orders what comes
// back.
//
// Role decides which panels are attempted; a panel the caller may not read is
// hidden rather than allowed to break the page. Clicking a stage bar slices the
// drill-down list — that is a filter passed to the invoice list, not a
// client-side re-slice of rows we happen to be holding.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dropdown,
  Field,
  Option,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import type { Session } from '../lib/identity'
import type {
  AnalyticsAging,
  AnalyticsCorrections,
  AnalyticsDenials,
  AnalyticsOverview,
  AnalyticsParams,
  AnalyticsSpendByEntity,
  AnalyticsThroughput,
} from '../api/types'
import type { ExportBatch, Company, Invoice } from '../lib/types'
import {
  loadAnalyticsAging,
  loadAnalyticsCorrections,
  loadAnalyticsDenials,
  loadAnalyticsOverview,
  loadAnalyticsSpendByEntity,
  loadAnalyticsThroughput,
  loadArchivePage,
  loadRecentExports,
  searchCompanies,
  searchVendors,
} from '../lib/data'
import {
  ACTIVE_STATUSES,
  STAGE_LABELS,
  TERMINAL_STATUSES,
  agingBands,
  bandColorKey,
  bandLabel,
  correctionRows,
  denialReasonRows,
  inFlightTotals,
  medianWaitRows,
  oldestBandCount,
  oldestWaiting,
  spendRows,
  stageRows,
  statusTotalsForNodes,
  terminalTotals,
  type Metric,
} from '../lib/metrics'
import { TIMELINE_NODES, type Role } from '../lib/constants'
import { emptyFilter, type InvoiceFilter } from '../lib/filters'
import { formatCurrency, formatDate, formatDateTime, ageInDays } from '../lib/format'
import { AGE_COLORS } from '../lib/theme'
import {
  PanelCard,
  KpiCard,
  HBarList,
  PairedColumns,
  LegendSwatch,
  type BarRowData,
  type ColumnPair,
} from '../components/DashboardCharts'
import { StatusBadge, AgePill } from '../components/Badges'
import { EntityPicker, type EntityOption } from '../components/EntityPicker'
import { Loading, ErrorBanner } from '../components/States'
import { ScreenTitle } from '../components/ScreenTitle'

const DRILL_CAP = 50
const DRILL_STATUSES = [...ACTIVE_STATUSES, ...TERMINAL_STATUSES]

type WindowKey = '30' | '90' | 'ytd'
const WINDOW_OPTIONS: { key: WindowKey; label: string }[] = [
  { key: '30', label: 'Last 30 days' },
  { key: '90', label: 'Last 90 days' },
  { key: 'ytd', label: 'Year to date' },
]

function windowStart(key: WindowKey, now: Date = new Date()): Date {
  if (key === 'ytd') return new Date(now.getFullYear(), 0, 1)
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - (key === '30' ? 30 : 90))
  return d
}

/** yyyy-MM-dd in local time — the window bound the API takes. */
function dateOnly(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

/** The fill for an age band at position `index` of `count`. The API defines the
    bands (§16.2 sends each one's day bounds), so the colour is chosen by
    position — youngest neutral through oldest red — and no threshold is
    hardcoded here. */
function bandFill(index: number, count: number): string {
  const key = bandColorKey(index, count)
  return key === 'neutral' ? tokens.colorNeutralStroke1 : AGE_COLORS[key]
}

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '8px' },
  sub: { color: tokens.colorNeutralForeground3 },
  filterBar: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px' },
  filterField: { flex: '1 1 160px', minWidth: '160px', maxWidth: '240px' },
  // Fluent Dropdown's ~250px min-width overflows narrow fields; clear it.
  control: { width: '100%', minWidth: 0 },
  toggleGroup: { display: 'flex', gap: '4px' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' },
  twoCol: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px' },
  legendRow: { display: 'flex', gap: '10px', color: tokens.colorNeutralForeground3 },
  panelNote: { display: 'block', marginTop: '10px', color: tokens.colorNeutralForeground3 },
  drillTable: { tableLayout: 'fixed', width: '100%' },
  right: { textAlign: 'right' },
  rightHeader: { '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end' } },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  empty: { padding: '48px 0', color: tokens.colorNeutralForeground3 },
  exportsRow: { display: 'grid', gridTemplateColumns: '1fr 80px 100px', gap: '8px', padding: '4px 0' },
})

/** The six aggregates plus the two row reads. A null slot means the panel was
    skipped for this role or the read failed — either way it does not render. */
interface DashData {
  overview: AnalyticsOverview | null
  aging: AnalyticsAging | null
  throughput: AnalyticsThroughput | null
  denials: AnalyticsDenials | null
  spend: AnalyticsSpendByEntity | null
  corrections: AnalyticsCorrections | null
  exports: ExportBatch[] | null
}

interface Props {
  session: Session
  refreshNonce?: number
}

export function DashboardScreen({ session, refreshNonce = 0 }: Props) {
  const s = useStyles()
  const [data, setData] = useState<DashData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [windowKey, setWindowKey] = useState<WindowKey>('30')
  const [metric, setMetric] = useState<Metric>('count')
  const [company, setCompany] = useState<{ id: string; name: string } | null>(null)
  const [vendor, setVendor] = useState<{ id: string; name: string } | null>(null)
  const [companyOptions, setCompanyOptions] = useState<Company[]>([])
  // The one cross-filter a click still sets: a stage, applied to the drill list.
  const [sliceStatus, setSliceStatus] = useState('')

  const [drill, setDrill] = useState<Invoice[] | null>(null)

  const roles = useMemo<Set<Role>>(() => new Set(session.roles), [session.roles])
  const isInternal = session.isInternal

  const startDate = useMemo(() => windowStart(windowKey), [windowKey])

  const params = useMemo<AnalyticsParams>(
    () => ({
      from: dateOnly(startDate),
      company_id: company ? [company.id] : undefined,
      vendor_id: vendor ? [vendor.id] : undefined,
    }),
    [startDate, company, vendor],
  )

  // The company filter's options: the master, not the rows on screen. The old
  // dashboard derived them from whatever it had loaded, which meant a company
  // with nothing in flight silently could not be selected.
  useEffect(() => {
    searchCompanies('', 200)
      .then(setCompanyOptions)
      .catch(() => setCompanyOptions([]))
  }, [])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Lazy so the rejection exists only in the slots that await it — one shared
    // rejected promise logs an unhandled rejection when a slot skips it.
    const skip = () => Promise.reject(new Error('skipped'))
    const seesCorrections = isInternal || roles.has('Training')
    const [overview, aging, throughput, denials, spend, corrections, exports] = await Promise.allSettled([
      loadAnalyticsOverview(params),
      loadAnalyticsAging(params),
      loadAnalyticsThroughput(params),
      isInternal ? loadAnalyticsDenials(params) : skip(),
      roles.has('ImportControl') ? loadAnalyticsSpendByEntity(params) : skip(),
      seesCorrections ? loadAnalyticsCorrections(params) : skip(),
      roles.has('ExportControl') ? loadRecentExports() : skip(),
    ])
    if (overview.status === 'rejected' && aging.status === 'rejected') {
      setError(errorMessage(overview.reason))
      setData(null)
    } else {
      setData({
        overview: overview.status === 'fulfilled' ? overview.value : null,
        aging: aging.status === 'fulfilled' ? aging.value : null,
        throughput: throughput.status === 'fulfilled' ? throughput.value : null,
        denials: denials.status === 'fulfilled' ? denials.value : null,
        spend: spend.status === 'fulfilled' ? spend.value : null,
        corrections: corrections.status === 'fulfilled' ? corrections.value : null,
        exports: exports.status === 'fulfilled' ? exports.value : null,
      })
    }
    setLoading(false)
  }, [params, roles, isInternal])

  useEffect(() => {
    void reload()
  }, [reload, refreshNonce])

  // The drill-down list. Same filter, sent as query parameters, so a stage slice
  // narrows the rows server-side instead of hiding some of a set we hold.
  useEffect(() => {
    const filter: InvoiceFilter = {
      ...emptyFilter,
      company: company?.name ?? '',
      companyId: company?.id ?? '',
      vendor: vendor?.name ?? '',
      vendorId: vendor?.id ?? '',
      fromDate: dateOnly(startDate),
    }
    let cancelled = false
    loadArchivePage(sliceStatus ? [sliceStatus] : DRILL_STATUSES, filter, undefined, DRILL_CAP)
      .then((page) => {
        if (!cancelled) setDrill(page.rows)
      })
      .catch(() => {
        if (!cancelled) setDrill([])
      })
    return () => {
      cancelled = true
    }
  }, [company, vendor, startDate, sliceStatus, refreshNonce])

  const fmt = useCallback(
    (v: number) => (metric === 'count' ? String(Math.round(v)) : compactUsd.format(v)),
    [metric],
  )

  // ---- KPIs ------------------------------------------------------------------
  // The overview reports counts and dollars BY STATUS; the in-flight and
  // reached-Point-Click-Care figures are folds over that breakdown, not fields
  // the API sends (metrics.ts). The API has already trimmed every count to the
  // rows this user may see.
  const kpis = useMemo(() => {
    const ov = data?.overview
    const nodes = data?.aging?.nodes
    const myNodes = new Set(TIMELINE_NODES.filter((n) => !n.archive && roles.has(n.role)).map((n) => n.key))
    const inFlight = inFlightTotals(ov)
    const terminal = terminalTotals(ov)
    const mine = statusTotalsForNodes(ov, myNodes)
    const bands = agingBands(nodes)
    const oldestBand = bands[bands.length - 1]
    return {
      inFlightCount: inFlight.count,
      inFlightTotal: inFlight.total,
      mineCount: mine.count,
      mineOverdue: oldestBandCount(nodes, myNodes),
      oldestBandText: oldestBand ? bandLabel(oldestBand) : 'the oldest band',
      oldest: oldestWaiting(nodes),
      terminalCount: Number(ov?.terminal_in_window ?? 0),
      terminalTotal: terminal.total,
      unresolvedFlags: Number(ov?.unresolved_flag_count ?? 0),
      avgDays: ov?.average_days_to_terminal ?? null,
    }
  }, [data, roles])

  // ---- Panels ----------------------------------------------------------------
  const bands = useMemo(() => agingBands(data?.aging?.nodes), [data])

  const pipelineRows = useMemo<BarRowData[]>(
    () =>
      stageRows(data?.overview?.by_status, data?.aging?.nodes, metric).map((r) => ({
        key: r.status,
        label: r.label,
        total: r.total,
        display: fmt(r.total),
        // Under the money measure there are no per-band dollars to draw (§16.2
        // is counts only), so the bar is one solid segment rather than a split
        // the API never reported.
        segments: r.bands.length
          ? r.bands.map((value, i) => ({ color: bandFill(i, r.bands.length), value }))
          : [{ color: tokens.colorNeutralStroke1, value: r.total }],
        title: `${r.label}: ${fmt(r.total)}`,
      })),
    [data, metric, fmt],
  )

  // Restored panel: the source drew a median-days-per-stage bar. §16.2 reports
  // banded counts, so this reports the band the middle invoice of each stage sits
  // in — a band, never a day figure the API did not send.
  const waitRows = useMemo<BarRowData[]>(
    () =>
      medianWaitRows(data?.aging?.nodes).map((r) => ({
        key: r.node,
        label: r.label,
        total: r.bandIndex + 1,
        display: r.bandLabel,
        segments: [{ color: bandFill(r.bandIndex, bands.length || 4), value: r.bandIndex + 1 }],
        title: `${r.label}: typically ${r.bandLabel} (${r.count} waiting)`,
      })),
    [data, bands.length],
  )

  // Received against completed, by week. The aggregate counts documents, so this
  // panel ignores the $ toggle rather than pretending to honour it.
  const trendPairs = useMemo<ColumnPair[]>(
    () =>
      (data?.throughput?.weeks ?? []).map((w) => ({
        key: w.week_start,
        a: Number(w.received ?? 0),
        b: Number(w.completed ?? 0),
        title: `Week of ${formatDate(w.week_start)} — received ${w.received ?? 0}, to Point Click Care ${w.completed ?? 0}`,
      })),
    [data],
  )

  const denialPanel = useMemo(() => {
    if (!data?.denials) return null
    const breakdown = denialReasonRows(data.denials)
    const rows: BarRowData[] = breakdown.rows.map((r) => ({
      key: r.key,
      label: r.key,
      total: r.value,
      display: String(r.value),
      segments: [{ color: AGE_COLORS.red, value: r.value }],
      title: `${r.key}: ${r.value}`,
    }))
    const steps: BarRowData[] = breakdown.steps.map((r) => ({
      key: r.key,
      label: r.key,
      total: r.value,
      display: String(r.value),
      segments: [{ color: tokens.colorNeutralStroke1, value: r.value }],
      title: `${r.key}: ${r.value}`,
    }))
    // `breakdown.categorized` is dropped here — its only consumer was a note
    // this memo used to render, since removed. `lib/metrics.ts` still computes
    // it; that is its own contract and unaffected by this screen's return shape.
    return { rows, steps, total: breakdown.total }
  }, [data])

  const entityRows = useMemo<BarRowData[]>(
    () =>
      spendRows(data?.spend).map((r) => ({
        key: r.key,
        label: r.key,
        total: r.value,
        display: compactUsd.format(r.value),
        segments: [{ color: tokens.colorBrandBackground, value: r.value }],
        title: `${r.key}: ${compactUsd.format(r.value)}`,
      })),
    [data],
  )

  const correctionPanel = useMemo(() => {
    if (!data?.corrections) return null
    const c = correctionRows(data.corrections)
    const bars = (rows: { key: string; value: number }[], color: string): BarRowData[] =>
      rows.map((r) => ({
        key: r.key,
        label: r.key,
        total: r.value,
        display: String(r.value),
        segments: [{ color, value: r.value }],
        title: `${r.key}: ${r.value}`,
      }))
    return {
      fields: bars(c.fields, AGE_COLORS.orange),
      vendors: bars(c.vendors, AGE_COLORS.yellow),
      sources: bars(c.sources, tokens.colorNeutralStroke1),
      // Restored panel: flag precision, per flag type. §16.6 sends this as an
      // array; the port had collapsed it to one number in a caption.
      flagPrecision: bars(c.flagPrecision, AGE_COLORS.red),
      total: c.total,
      flagsResolvedClean: c.flagsResolvedClean,
    }
  }, [data])

  return (
    <div className={s.root}>
      <ScreenTitle>Analytics</ScreenTitle>

      <div className={s.filterBar}>
        <Field label="Window" className={s.filterField}>
          <Dropdown
            className={s.control}
            value={WINDOW_OPTIONS.find((w) => w.key === windowKey)?.label}
            selectedOptions={[windowKey]}
            onOptionSelect={(_, d) => setWindowKey((d.optionValue as WindowKey) ?? '30')}
          >
            {WINDOW_OPTIONS.map((w) => (
              <Option key={w.key} value={w.key}>{w.label}</Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Company" className={s.filterField}>
          <Dropdown
            className={s.control}
            placeholder="All companies"
            value={company?.name ?? ''}
            selectedOptions={company ? [company.id] : []}
            onOptionSelect={(_, d) => {
              const picked = companyOptions.find((c) => c.company_id === d.optionValue)
              setCompany(picked ? { id: picked.company_id, name: picked.company_name } : null)
            }}
          >
            <Option value="">All companies</Option>
            {companyOptions.map((c) => (
              <Option key={c.id} value={c.company_id} text={c.company_name}>{c.company_name}</Option>
            ))}
          </Dropdown>
        </Field>
        {/* Vendor searches the master server-side — there are ~2,700 of them, so a
            dropdown of every one was never an option. */}
        <Field label="Vendor" className={s.filterField}>
          <EntityPicker
            size="small"
            placeholder="All vendors"
            value={vendor ? { id: vendor.id, name: vendor.name } : undefined}
            search={(t) =>
              searchVendors(t).then<EntityOption[]>((vs) =>
                vs.map((v) => ({
                  id: v.vendor_id,
                  primary: v.vendor_name,
                  secondary: [v.city, v.state_or_province].filter(Boolean).join(', ') || undefined,
                })),
              )
            }
            onSelect={(o) => setVendor({ id: o.id, name: o.primary })}
          />
        </Field>
        <Field label="Measure">
          <div className={s.toggleGroup}>
            <Button size="small" appearance={metric === 'count' ? 'primary' : 'secondary'} onClick={() => setMetric('count')}>
              Count
            </Button>
            <Button size="small" appearance={metric === 'amount' ? 'primary' : 'secondary'} onClick={() => setMetric('amount')}>
              $
            </Button>
          </div>
        </Field>
      </div>

      {(sliceStatus || company || vendor) && (
        <div className={s.chips}>
          {sliceStatus && (
            <Button size="small" icon={<DismissRegular />} iconPosition="after" onClick={() => setSliceStatus('')}>
              Stage: {STAGE_LABELS[sliceStatus] ?? sliceStatus}
            </Button>
          )}
          {company && (
            <Button size="small" icon={<DismissRegular />} iconPosition="after" onClick={() => setCompany(null)}>
              Company: {company.name}
            </Button>
          )}
          {vendor && (
            <Button size="small" icon={<DismissRegular />} iconPosition="after" onClick={() => setVendor(null)}>
              Vendor: {vendor.name}
            </Button>
          )}
        </div>
      )}

      <ErrorBanner message={error} />
      {loading ? (
        <Loading label="Loading metrics…" />
      ) : !data ? null : kpis.inFlightCount === 0 && kpis.terminalCount === 0 ? (
        <div className={s.empty}>
          <Body1>No invoices are visible to you yet.</Body1>
        </div>
      ) : (
        <>
          <div className={s.kpiGrid}>
            <KpiCard
              label="In flight"
              value={String(kpis.inFlightCount)}
              sub={formatCurrency(kpis.inFlightTotal)}
            />
            <KpiCard
              label="Awaiting my action"
              value={String(kpis.mineCount)}
              sub={
                kpis.mineOverdue > 0
                  ? `${kpis.mineOverdue} at ${kpis.oldestBandText}`
                  : `none at ${kpis.oldestBandText}`
              }
              subDanger={kpis.mineOverdue > 0}
            />
            <KpiCard
              label="Oldest waiting"
              value={kpis.oldest ? bandLabel(kpis.oldest.band) : '—'}
              sub={kpis.oldest?.label}
            />
            <KpiCard
              label={`To Point Click Care, ${WINDOW_OPTIONS.find((w) => w.key === windowKey)?.label.toLowerCase()}`}
              value={String(kpis.terminalCount)}
              sub={`${formatCurrency(kpis.terminalTotal)}${
                kpis.avgDays === null ? '' : ` · ${kpis.avgDays.toFixed(1)}d average`
              }`}
            />
          </div>

          <PanelCard
            title="Where invoices are"
            hint={
              metric === 'count' ? (
                <span className={s.legendRow}>
                  {bands.map((b, i) => (
                    <LegendSwatch key={b.label} color={bandFill(i, bands.length)} label={bandLabel(b)} />
                  ))}
                </span>
              ) : (
                'dollar totals — age bands are counts only'
              )
            }
          >
            {pipelineRows.length === 0 ? (
              <Caption1 className={s.sub}>Nothing in flight matches the current filters.</Caption1>
            ) : (
              <HBarList
                rows={pipelineRows}
                selectedKey={sliceStatus || null}
                onSelect={(k) => setSliceStatus((prev) => (prev === k ? '' : k))}
              />
            )}
            {kpis.unresolvedFlags > 0 && (
              <Caption1 className={s.panelNote}>
                {kpis.unresolvedFlags} unresolved flag{kpis.unresolvedFlags === 1 ? '' : 's'} across these invoices.
              </Caption1>
            )}
          </PanelCard>

          <div className={s.twoCol}>
            {/* This panel's hint is a LEGEND, not help text, so it survives
                the 2026-08-13 clean-out. The bar length here is an ordinal
                band index, never a day count — the API sends no day figure —
                so without it a reader takes bars of length 1, 2, 3 for
                magnitudes under a title that promises a duration. */}
            {waitRows.length > 0 && (
              <PanelCard
                title="How long each stage takes"
                hint="the band the middle invoice of each stage sits in"
              >
                <HBarList rows={waitRows} />
              </PanelCard>
            )}

            {/* The legend's third item is a caveat, not help text: this panel
                counts documents and deliberately ignores the Measure toggle,
                so without it a user who switches to $ sees counts and is told
                nothing. */}
            <PanelCard
              title="Intake vs to Point Click Care, weekly"
              hint={
                <span className={s.legendRow}>
                  <LegendSwatch color={tokens.colorBrandBackground} label="received" />
                  <LegendSwatch color={tokens.colorNeutralStroke1} label="completed" />
                  <span className={s.sub}>documents, either measure</span>
                </span>
              }
            >
              {trendPairs.length === 0 ? (
                <Caption1 className={s.sub}>Nothing was received in this window.</Caption1>
              ) : (
                <PairedColumns
                  pairs={trendPairs}
                  aColor={tokens.colorBrandBackground}
                  bColor={tokens.colorNeutralStroke1}
                  startLabel={formatDate(startDate.toISOString())}
                  endLabel="Today"
                />
              )}
            </PanelCard>

            {data.spend && (
              <PanelCard title="Spend by entity" badge="Import Control">
                {entityRows.length === 0 ? (
                  <Caption1 className={s.sub}>No tagged entities in this window.</Caption1>
                ) : (
                  <HBarList rows={entityRows} />
                )}
              </PanelCard>
            )}

            {denialPanel && (
              <PanelCard
                title="Why invoices are denied"
                badge="Internal roles"
                hint={`${denialPanel.total} denied or archived in window`}
              >
                {denialPanel.rows.length === 0 ? (
                  <Caption1 className={s.sub}>Nothing was denied or archived in this window.</Caption1>
                ) : (
                  <HBarList rows={denialPanel.rows} />
                )}
              </PanelCard>
            )}

            {denialPanel && denialPanel.steps.length > 0 && (
              <PanelCard title="Where invoices are denied" badge="Internal roles">
                <HBarList rows={denialPanel.steps} />
              </PanelCard>
            )}

            {data.exports && data.exports.length > 0 && (
              <PanelCard title="Recent exports" badge="Export Control">
                {data.exports.map((e) => (
                  <div key={e.id} className={s.exportsRow}>
                    <Caption1 className={s.ellipsis}>{formatDateTime(e.exported_at)}</Caption1>
                    <Caption1>{e.record_count ?? '—'} inv.</Caption1>
                    <Badge appearance="tint" size="small" color={e.status === 'Completed' ? 'success' : e.status === 'Failed' ? 'danger' : 'informative'}>
                      {e.status ?? 'Unknown'}
                    </Badge>
                  </div>
                ))}
              </PanelCard>
            )}

            {correctionPanel && (
              <PanelCard
                title="Correction rate"
                badge="References"
                hint={`${correctionPanel.total} field corrections in window`}
              >
                {correctionPanel.fields.length === 0 ? (
                  <Caption1 className={s.sub}>No corrections captured in this window.</Caption1>
                ) : (
                  <HBarList rows={correctionPanel.fields} />
                )}
              </PanelCard>
            )}

            {correctionPanel && correctionPanel.flagPrecision.length > 0 && (
              <PanelCard
                title="Which flags cry wolf"
                badge="References"
                hint={`${correctionPanel.flagsResolvedClean} cleared with no correction`}
              >
                <HBarList rows={correctionPanel.flagPrecision} />
              </PanelCard>
            )}

            {correctionPanel && correctionPanel.vendors.length > 0 && (
              <PanelCard title="Corrections by vendor" badge="References">
                <HBarList rows={correctionPanel.vendors} />
              </PanelCard>
            )}

            {correctionPanel && correctionPanel.sources.length > 0 && (
              <PanelCard title="What the corrected value came from" badge="References">
                <HBarList rows={correctionPanel.sources} />
              </PanelCard>
            )}
          </div>

          <PanelCard
            title="Invoices in this view"
            hint={`${drill === null ? 'loading' : `showing ${drill.length}`} · most recent ${DRILL_CAP}, oldest first`}
          >
            <Table size="small" className={s.drillTable}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell style={{ width: '20%' }}>Company</TableHeaderCell>
                  <TableHeaderCell style={{ width: '22%' }}>Vendor</TableHeaderCell>
                  <TableHeaderCell style={{ width: '14%' }}>Date</TableHeaderCell>
                  <TableHeaderCell style={{ width: '8%' }}>Age</TableHeaderCell>
                  <TableHeaderCell style={{ width: '24%' }}>Status</TableHeaderCell>
                  <TableHeaderCell style={{ width: '12%' }} className={s.rightHeader}>Total</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...(drill ?? [])]
                  .sort((a, b) => (ageInDays(b.invoice_date) ?? -1) - (ageInDays(a.invoice_date) ?? -1))
                  .map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className={s.ellipsis}>{inv.company_name ?? '—'}</TableCell>
                      <TableCell className={s.ellipsis}>{inv.vendor_name ?? '—'}</TableCell>
                      <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                      <TableCell><AgePill days={ageInDays(inv.invoice_date)} /></TableCell>
                      <TableCell className={s.ellipsis}><StatusBadge status={inv.status} /></TableCell>
                      <TableCell className={s.right}>{formatCurrency(inv.total)}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </PanelCard>
        </>
      )}
    </div>
  )
}
